import { useEffect, useState } from "react";
import API from "@/lib/api";

/**
 * Live "Spend on real perks" tile for the landing page — swaps the
 * hardcoded "$10 off → tees → $100 credit" copy for a snapshot of the
 * real rewards catalogue. Renders the cheapest coupon reward and the
 * highest-value credit / merch reward as a range, plus a small live
 * count. Falls back to reasonable copy while loading / on error so the
 * tile never looks empty.
 *
 * Backend: `GET /api/stepathon/rewards` returns active rewards with
 * `name`, `points_cost`, `reward_type`, `amount_dollars`. Test rows
 * (`name` starts with `TEST_`) are filtered out client-side.
 */
export default function LandingRewardsPerkTile() {
  const [rewards, setRewards] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await API.get("/stepathon/rewards");
        if (cancelled) return;
        const clean = (data?.rewards || []).filter(
          (r) => r && !String(r.name || "").startsWith("TEST_") && r.status !== "draft"
        );
        setRewards(clean);
      } catch { /* silent — tile keeps its fallback copy */ }
    })();
    return () => { cancelled = true; };
  }, []);

  if (rewards === null) {
    return (
      <>
        <h3 className="mt-2 text-lg font-extrabold">Loading live rewards…</h3>
        <p className="mt-1 text-sm text-purple-100/80">Coupon codes, refill credits, and Normalize the Journey merch — refreshed from the live catalogue.</p>
      </>
    );
  }

  if (rewards.length === 0) {
    return (
      <>
        <h3 className="mt-2 text-lg font-extrabold">New rewards dropping soon</h3>
        <p className="mt-1 text-sm text-purple-100/80">Coupon codes, refill credits, and Normalize the Journey merch drop into the catalogue as they&apos;re ready.</p>
      </>
    );
  }

  const cheapest = rewards.reduce((a, r) => (r.points_cost < a.points_cost ? r : a), rewards[0]);
  const priciest = rewards.reduce((a, r) => (r.points_cost > a.points_cost ? r : a), rewards[0]);
  const label = (r) => {
    const dollars = Number(r.amount_dollars || 0);
    // Backend uses `free_refill` as the reward_type for refill-credit
    // rewards. Older rows used `credit`. Treat both the same so the
    // priciest tier ($100 refill credit · Legend tier) renders as
    // "$100 credit" instead of falling through to a truncated raw name.
    if (r.reward_type === "coupon" && dollars > 0) return `$${dollars} off`;
    if ((r.reward_type === "free_refill" || r.reward_type === "credit") && dollars > 0) return `$${dollars} credit`;
    if (r.reward_type === "merch") {
      const n = r.name || "GLPFlo merch";
      // Strip the boilerplate "Normalize the Journey" prefix if present so
      // the range headline stays punchy on narrow screens.
      return n.replace(/^Normalize the Journey\s+/i, "").slice(0, 22) || "GLPFlo merch";
    }
    return (r.name || "Reward").slice(0, 24);
  };

  return (
    <>
      <h3 className="mt-2 text-lg font-extrabold" data-testid="landing-rewards-range">
        {label(cheapest)} → {label(priciest)}
      </h3>
      <p className="mt-1 text-sm text-purple-100/80">
        {rewards.length} live reward{rewards.length === 1 ? "" : "s"} in the catalogue right now
        {cheapest.points_cost > 0 ? ` — starting at ${cheapest.points_cost.toLocaleString()} pts` : ""}.
      </p>
    </>
  );
}
